// Overview — 真實資料版（PR #87 onwards）。
//
// 資料來源：tenant-api `GET /v1/dashboard/overview`，backed by
//   - daily_site_rollups (KPI completion / overdue)
//   - incidents (KPI incidents / recent list)
//   - patrol_sessions / patrol_events (active officers / hourly)
//   - audit_logs + report_jobs (activity feed)
//
// UI 狀態：loading skeleton → success → friendly error fallback (with retry)
//          → empty state（情境化中文，不出現技術術語）。
import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  Siren,
  Users,
  RefreshCw,
  ArrowRight,
  Inbox,
  ServerCrash,
  Building2,
} from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import PageHeader from "../components/PageHeader.js";
import MetricCard from "../components/MetricCard.js";
import CompletionChart from "../components/CompletionChart.js";
import IncidentTable from "../components/IncidentTable.js";
import SiteHealthCard from "../components/SiteHealthCard.js";
import ActivityFeed from "../components/ActivityFeed.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import {
  fetchOverview,
  friendlyDashboardError,
  type OverviewResponse,
} from "../lib/dashboardApi.js";
import type { Severity, Site, Incident, ActivityItem } from "../types/dashboard.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useAiMode } from "../AiModeContext.js";

function severityToUi(s: OverviewResponse["recent_incidents"][number]["severity"]): Severity {
  // Backend uses 'med'; UI mock type uses 'medium'.
  return s === "med" ? "medium" : s;
}

function relativeFromIso(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  const day = Math.floor(hr / 24);
  return `${day} 天前`;
}

function watchSiteToUi(s: OverviewResponse["watch_sites"][number]): Site {
  return {
    id: s.id,
    name: s.name,
    client: s.client,
    health: s.health,
    completion: s.completion,
    activeOfficers: s.active_officers,
    lastTap: relativeFromIso(s.last_tap),
  };
}

function incidentToUi(i: OverviewResponse["recent_incidents"][number]): Incident {
  return {
    id: i.id,
    ts: i.ts,
    site: i.site,
    location: i.location,
    severity: severityToUi(i.severity),
    status: i.status,
    message: i.title || i.body,
    officer: undefined,
  };
}

function activityToUi(a: OverviewResponse["activity"][number]): ActivityItem {
  return {
    id: a.id,
    ts: relativeFromIso(a.ts),
    kind: a.kind,
    message: a.message,
    site: a.site ?? undefined,
  };
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; data: OverviewResponse }
  | { status: "error"; message: string };

export default function Overview(): React.ReactElement {
  const session = useDashboardSession();
  // AI mode lands on the AI 導覽 page (/dashboard/topology) — chat is
  // the central workspace, the legacy KPI overview is the「professional
  // mode」 surface. New tenant_admin who just LINE-bound got auto-
  // logged-in (PR #502/#503/#505 chain) but were dropped on /dashboard/
  // root, which renders this Overview and looks empty until they have
  // sites/incidents. Redirecting in-component keeps the route map
  // identical (Overview stays at the index path; pro mode users see it
  // as before) and avoids touching the login-redirect logic.
  const { mode } = useAiMode();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const tenantParam = session.super_admin
    ? session.acting_tenant_id ?? null
    : null;

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await fetchOverview({ tenantId: tenantParam });
      setState({ status: "ready", data });
    } catch (err) {
      setState({ status: "error", message: friendlyDashboardError(err) });
    }
  }, [tenantParam]);

  useEffect(() => {
    void load();
  }, [load]);

  // AI mode → redirect to AI 導覽. See top-of-function comment for why.
  // Placed AFTER all hook declarations to keep the hook-order stable.
  if (mode === "ai") {
    return <Navigate to="/dashboard/topology" replace />;
  }

  // super_admin 未挑 acting tenant 的引導文案
  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader
          title="總覽"
          description="即時案場完成率、漏巡、高風險點與最新事件"
        />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="總覽"
        description="即時案場完成率、漏巡、高風險點與最新事件"
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            <Link to="/dashboard/monitor" className="btn-primary text-sm">
              即時監控 <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        }
      />
      {state.status === "loading" && <OverviewSkeleton />}
      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="畫面暫時載入不出來"
          description={state.message}
          action={
            <button className="btn-primary text-sm" onClick={() => void load()}>
              再試一次
            </button>
          }
        />
      )}
      {state.status === "ready" && <OverviewBody data={state.data} />}
    </>
  );
}

function OverviewBody({ data }: { data: OverviewResponse }): React.ReactElement {
  const watchSites = data.watch_sites.map(watchSiteToUi);
  const incidents = data.recent_incidents.map(incidentToUi);
  const activity = data.activity.map(activityToUi);
  const allZeroKpi =
    data.kpi.completion.value === 0 &&
    data.kpi.missed.value === 0 &&
    data.kpi.incidents.value === 0 &&
    data.kpi.officers.value === 0;

  return (
    <>
      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label={data.kpi.completion.label}
          value={data.kpi.completion.value}
          suffix={data.kpi.completion.suffix}
          trend={data.kpi.completion.trend}
          icon={CheckCircle2}
        />
        <MetricCard
          label={data.kpi.missed.label}
          value={data.kpi.missed.value}
          suffix={data.kpi.missed.suffix}
          trend={data.kpi.missed.trend}
          trendInverse
          icon={AlertTriangle}
        />
        <MetricCard
          label={data.kpi.incidents.label}
          value={data.kpi.incidents.value}
          suffix={data.kpi.incidents.suffix}
          trend={data.kpi.incidents.trend}
          trendInverse
          icon={Siren}
        />
        <MetricCard
          label={data.kpi.officers.label}
          value={data.kpi.officers.value}
          suffix={data.kpi.officers.suffix}
          trend={data.kpi.officers.trend}
          icon={Users}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-text-primary">24 小時完成率</h2>
              <p className="text-xs text-text-secondary mt-0.5">
                {data.cached ? "快取結果（最多延遲 60 秒）" : "即時統計，每 5 分鐘更新"}
              </p>
            </div>
            <span className="badge-accent">{data.date_local}</span>
          </div>
          <CompletionChart data={data.hourly_series} />
        </div>

        <div className="card p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-text-primary">最新動態</h2>
            <Link to="/dashboard/audit" className="text-xs text-accent hover:text-accent-hover">
              全部操作紀錄 →
            </Link>
          </div>
          {activity.length === 0 ? (
            <EmptyState icon={Inbox} title="今天還沒有動態" description="當第一筆巡邏完成或事件回報後，這裡會即時顯示。" />
          ) : (
            <ActivityFeed items={activity} />
          )}
        </div>
      </div>

      {/* Watch sites */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-text-primary">需要關注的案場</h2>
          <Link to="/dashboard/sites" className="text-xs text-accent hover:text-accent-hover">
            全部案場 →
          </Link>
        </div>
        {watchSites.length === 0 ? (
          <EmptyState icon={Building2} title="目前沒有需要特別注意的案場" description="所有案場今日完成率都在標準之上，繼續保持。" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {watchSites.map((s) => (
              <SiteHealthCard key={s.id} site={s} />
            ))}
          </div>
        )}
      </div>

      {/* Recent incidents */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-text-primary">近期事件</h2>
          <Link to="/dashboard/monitor" className="text-xs text-accent hover:text-accent-hover">
            全部事件 →
          </Link>
        </div>
        {incidents.length === 0 ? (
          <EmptyState icon={Inbox} title="近 7 天沒有事件" description="如果有任何巡邏異常或漏巡，事件會在這裡列出。" />
        ) : (
          <div className="card overflow-hidden">
            <IncidentTable incidents={incidents.slice(0, 4)} compact />
          </div>
        )}
      </div>

      {allZeroKpi && (
        <p className="mt-6 text-xs text-text-muted">
          目前統計數字都是 0，可能是今日巡邏尚未開始，或這個單位剛建立還沒有資料。
        </p>
      )}
    </>
  );
}

function OverviewSkeleton(): React.ReactElement {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-8 w-32" />
            <Skeleton className="mt-3 h-3 w-16" />
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="mt-4 h-64 w-full" />
        </div>
        <div className="card p-5">
          <Skeleton className="h-4 w-24" />
          <div className="mt-4 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-7 w-7 rounded-md" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-2 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card p-4 space-y-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-2 w-full" />
          </div>
        ))}
      </div>
    </>
  );
}
